import React, { useEffect, useState, useContext } from 'react';
import { IRootState } from 'src/reducers';
import { useSelector } from 'react-redux';
import { Url } from 'src/api';
import Card from 'src/components/common/Card.redesign';
import VisibilityLoader from 'src/components/common/VisibilityLoader';
import useFetch from 'use-http';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import EasternTownship from 'src/models/EasternTownship';
import MapCard from './MapCard';
import { EasternTownshipTypeArray } from 'src/models/EasternTownshipType';

interface IEasternTownshipCardProps {
    townshipId: number;
    isActive: boolean;
    onTownshipSelected: (townshipId: number) => void;
    onMapSelected: (sectionNumber: number) => void;
    reoloadTownships: (townshipId: number) => void;
}

const EasternTownshipCard: React.FC<IEasternTownshipCardProps> = (props) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const dialogContext = useContext<dialogCreators>(DialogContext);
    const [township, setTownship] = useState<EasternTownship>(undefined);
    const [mapsLoaded, setMapsLoaded] = useState(false);
    const [mapIds, setMapIds] = useState([]);
    const [selectedMap, setSelectedMap] = useState(0);

    const { get, response } = useFetch();

    const loadData = async (isVisible: boolean) => {
        if (isVisible && !township) {
            const result = await get(`${Url.project.getEasternTownship(appContext, props.townshipId)}`);

            if (response.ok) {
                setTownship(result);
            }
        }
    };

    const loadMaps = async () => {
        const data = await get(`${Url.project.getEasternMapsByTownship(appContext, props.townshipId)}?select=id`);

        if (response.ok) {
            setMapsLoaded(true);
            setMapIds(data);

            if (appContext.sectionNumber) {
                const idx = (data as number[]).indexOf(appContext.sectionNumber);
                setSelectedMap(data[idx]);
                props.onMapSelected(data[idx]);
            } else {
                setSelectedMap(data[0]);
                props.onMapSelected(data[0]);
            }
        }
    };

    useEffect(() => {
        if (props.isActive) {
            if (!mapsLoaded) {
                loadMaps();
            } else {
                props.onMapSelected(selectedMap);
            }
        }
    }, [props.isActive]);

    const onMapSelected = (mapNumber: number) => {
        setSelectedMap(mapNumber);
        props.onMapSelected(mapNumber);
    };

    const mapsPanel = () => {
        let sectionContent: React.ReactElement = undefined;

        if (!mapsLoaded) {
            sectionContent = <div>Loading...</div>;
        } else if (mapIds.length === 0) {
            sectionContent = (
                <div
                    style={{
                        padding: '1rem',
                        height: '4rem',
                        fontWeight: 'bold',
                        border: '2px solid red',
                        fontSize: '1.1em',
                        textAlign: 'center',
                        verticalAlign: 'center ',
                    }}
                >
                    No Maps
                </div>
            );
        } else {
            sectionContent = (
                <>
                    {mapIds.map((id) => (
                        <MapCard key={id} mapNumber={id} townshipId={props.townshipId} onMapSelected={onMapSelected} isActive={selectedMap === id} />
                    ))}
                </>
            );
        }

        return (
            <div style={{ border: '1px solid #ccc', maxHeight: '80rem', overflow: 'hidden', overflowY: 'auto', marginTop: '.5rem' }}>
                {sectionContent}
            </div>
        );
    };

    const content = () => {
        if (!township) {
            return <div>Loading...</div>;
        } else {
            return (
                <div>
                    <span className="title">
                        {township.name} -{' '}
                        {EasternTownshipTypeArray().filter((i: { id: number; name: string }) => i.id === township.easternTownshipTypeId)[0].name}
                    </span>
                    {props.isActive && mapsPanel()}
                </div>
            );
        }
    };

    const reloadData = async (townshipId: number) => {
        const result = await get(`${Url.project.getEasternTownship(appContext, props.townshipId)}`);

        if (response.ok) {
            setTownship(result);
        }
    };

    const actions: ContextMenuAction[] = [
        { text: 'Create Map', action: () => dialogContext.project.addUpdateEasternMap(props.townshipId, 0, 'create', loadMaps) },
        { text: 'Modify Township', action: () => dialogContext.project.addUpdateDeleteEasternTownship(township.id, 'edit', reloadData) },
    ];

    return (
        <Card
            contextActions={props.isActive && actions}
            newContext={true}
            showMenu={true}
            onClick={() => props.onTownshipSelected(props.townshipId)}
            rightBadgePanel={
                township && (
                    <span style={{ position: 'absolute', right: '3rem', fontSize: '.9em', fontWeight: 'bold', color: '#999' }}>
                        {township.mapCount} maps
                    </span>
                )
            }
        >
            <VisibilityLoader onVisibilityChanged={loadData}>
                <div style={{ padding: '1rem', position: 'relative' }}>{content()}</div>
            </VisibilityLoader>
        </Card>
    );
};

export default EasternTownshipCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/EasternTownshipCard.tsx